import { Injectable, inject } from '@angular/core';
import { RouterStateSnapshot, ActivatedRouteSnapshot, Router, CanActivateFn, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { firstValueFrom, Observable, of, merge, from } from 'rxjs';
import { catchError, filter, switchMap } from 'rxjs/operators';

import { Loggable } from 'app/utils/logging/loggable';

import { AuthService } from '../../services/auth.service';
import { LaunchDarklyService } from '../services/launch-darkly/launch-darkly.service';
import { AuthenticationState, coreSelectors } from '../state';

@Injectable({
  providedIn: 'root',
})
export class ScootaverseGuardService extends Loggable {
  public constructor(
    public router: Router,
    public store: Store,
    public ldService: LaunchDarklyService,
    public authService: AuthService,
  ) {
    super();
    this.setDisplayName('ScootaverseGuardService');
  }
}

export const canActivateScootaverses: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean | UrlTree> => {
  const scootaverseGuardService = inject(ScootaverseGuardService);
  const scootaverseUrl = scootaverseGuardService.router.createUrlTree(['home/scootaverses'], {});
  const classicUrl = scootaverseGuardService.router.createUrlTree(['home'], {});

  const $disableScootaverses = new Promise<boolean | UrlTree>((resolve) => {
    if (state.url.includes(scootaverseUrl.toString())) {
      return resolve(true);
    }
    return resolve(scootaverseUrl);
  });

  const signedOut$ = scootaverseGuardService.store.select(coreSelectors.getAuthenticationState).pipe(
    filter((authState) => authState === AuthenticationState.UNAUTHENTICATED),
    switchMap(() => of(true)),
  );

  const signedIn$ = scootaverseGuardService.store.select(coreSelectors.getUserAuthenticationState).pipe(
    filter(({ state: authState }) => authState === AuthenticationState.AUTHENTICATED),
    switchMap(async ({ isGuest }): Promise<boolean> => {
      if (!isGuest) {
        return true;
      }
      await firstValueFrom(scootaverseGuardService.authService.signOutUser()).catch((error) => {
        scootaverseGuardService.error('Error signing out user', error);
      });
      return true;
    }),
    catchError((error: unknown): Observable<boolean> => {
      scootaverseGuardService.error('Error in canActivate', error);
      return of(true);
    }),
  );

  $disableScootaverses
    .then((url) => merge(signedIn$, signedOut$, of(url)))
    .catch((error) => {
      scootaverseGuardService.error('Error in canActivateScootaverses', error);
      return of(classicUrl);
    });
  return from($disableScootaverses);
};
